"use client";
import React, { useContext, useEffect, useState } from "react";

import { CurrentDeviceContext } from "@stadium/ui/src/contexts/responsive-content";

import CountryFlag from "@stadium/ui/src/shared/country-flag";
import useRouter from "@stadium/hooks/lib/use-custom-route";

import { getSelectedLanguage, isForceTranslationsEnabled } from "@stadium/services/weglot/helpers";
import CountrySelectorModal, {
  NewCountrySelectorModal,
} from "@stadium/ui/src/components/modal/country-selector-modal";
import BsDropdownMultiSelect from "@stadium/ui/src/components/dropdown/bs-dropdown-multiselect";
import Pill from "@stadium/ui/src/components/pill";

import { DROPDOWN_ID } from "@stadium/ui/src/constants/weglot-translator";
import { weglotNoTranslationClassName } from "@stadium/constants";

import countryDD from "../../assets/stylesheets/core/dropdown/countryDropdown.module.scss";

import styles from "./main.module.scss";

interface CountryProps {
  name: string;
  iso: string;
  id: number;
}
interface LanguageProps {
  key: string;
  label: string;
}
interface CountrySelectorProps {
  className?: string;
  selectedCountry: CountryProps;
  onClick: () => void;
  countries: CountryProps[];
  onSelectCountry: (country: CountryProps) => void;
  type: string;
  flagWidth: number;
  flagHeight: number;
  countryIsoClass?: string;
  labelText?: string;
  containerClass?: string;
  flagClassName?: string;
  weglotInstance?: object;
  availableLanguages?: LanguageProps[];
  onSelectLang?: (lang: LanguageProps) => void;
  langInUrl?: string;
  showSearchFilter: boolean;
  title: string;
  showCountryChangeModal?: boolean;
  arrowCustomClass?: string;
  dropdownLabel?: any;
  dropdownChildren?: any;
  parentLabel?: string;
  hideCountryFlag?: boolean;
}

const CountrySelector = (props: CountrySelectorProps) => {
  const {
    onClick,
    selectedCountry: defaultCountry,
    countries,
    onSelectCountry,
    type,
    flagWidth,
    flagHeight,
    countryIsoClass,
    containerClass,
    flagClassName,
    showSearchFilter,
    title,
    hideCountryFlag = false,
  } = props;

  const [isOpen, setIsOpen] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(defaultCountry);

  const toggleModal = () => {
    setIsOpen((prevState) => !prevState);
  };

  const submitModal = () => {
    onSelectCountry(selectedCountry);
    toggleModal();
  };

  //for case when user selects a country in modal but closes it without submitting.
  //if country change programatically then we need to change selected country.
  useEffect(() => {
    if (defaultCountry) {
      setSelectedCountry(defaultCountry);
    }
  }, [isOpen, defaultCountry]);

  const submitDisabled = defaultCountry.iso === selectedCountry.iso;

  return (
    <>
      <div
        className={`d-inline-flex nav-border-divider mobile-country-selector ${containerClass}`}
        onClick={() => {
          setIsOpen(true);
          onClick?.();
        }}
      >
        <div className="icon-location align-middle d-inline-flex" />{" "}
        <div className={`${countryDD.countryDropdown}`}>
          {!hideCountryFlag && (
            <CountryFlag
              country={defaultCountry}
              type={type}
              flagWidth={flagWidth}
              flagHeight={flagHeight}
              className={flagClassName}
            />
          )}
          <div className={`${countryDD.countryName}  ${countryIsoClass}`}>
            {defaultCountry?.iso}
          </div>
          <span
            className={`${countryDD.changeText} d-lg-none ml-auto text-uppercase text-underline font-weight-bold`}
          >
            Change
          </span>
          <i className="icon-chevron-down ml-2" />
        </div>
      </div>
      <CountrySelectorModal
        open={isOpen}
        closeModal={() => setIsOpen(false)}
        countries={countries}
        selectedCountry={selectedCountry}
        onClickSubmit={submitModal}
        setSelectedCountry={setSelectedCountry}
        type={type}
        submitDisabled={submitDisabled}
        flagWidth={flagWidth}
        flagHeight={flagHeight}
        showSearchFilter={showSearchFilter}
        title={title}
      />
    </>
  );
};

export const NewCountrySelector = (props: CountrySelectorProps) => {
  const { isDesktopDevice } = useContext(CurrentDeviceContext);
  const router = useRouter();

  const {
    onClick,
    selectedCountry: defaultCountry,
    countries,
    onSelectCountry,
    type,
    flagWidth,
    flagHeight,
    weglotInstance,
    availableLanguages,
    onSelectLang,
    langInUrl,
    showSearchFilter = true,
    title,
    showCountryChangeModal = true,
    labelText,
    currencies,
    currencySelectorProps = {},
    showLanguages = true,
    arrowCustomClass = "",
    dropdownLabel,
    dropdownChildren = "",
    parentLabel,
    hideCountryFlag = false,
  } = props;

  const [selectedLang, setSelectedLang] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(defaultCountry);
  const [confirmedCountry, setConfirmedCountry] = useState(defaultCountry);
  const [isDropDownOpen, setIsDropDownOpen] = useState(false);
  const [countriesToBeDisplayed, setCountriesToBeDisplayed] = useState(null);
  const [currenciesToBeDisplayed, setCurrenciesToBeDisplayed] = useState(null);

  //if country change programatically then we need to change selected country.
  useEffect(() => {
    if (defaultCountry) {
      setSelectedCountry(defaultCountry);
      setConfirmedCountry(defaultCountry);
    }
  }, [defaultCountry]);

  const toggleModal = () => {
    setIsOpen((prevState) => !prevState);
  };

  const submitModal = () => {
    if (defaultCountry.iso !== selectedCountry.iso) {
      setConfirmedCountry(selectedCountry);
      onSelectCountry(selectedCountry);
    }
    toggleModal();
  };

  const onFilter = (filteredOptions) => {
    setCountriesToBeDisplayed(filteredOptions);
  };

  const onCurrencyFilter = (filteredOptions) => {
    setCurrenciesToBeDisplayed(filteredOptions);
  };

  const handleToggle = (prevIsDropDownOpen) => {
    setIsDropDownOpen(prevIsDropDownOpen);
    onClick?.();
  };

  const handleClick = () => {
    setIsDropDownOpen((prevIsDropDownOpen) => !prevIsDropDownOpen);
    onClick?.();
  };
  const handleLanguageSelection = (languageToBeSelected) => {
    weglotInstance?.switchTo(languageToBeSelected);
    onSelectLang(languageToBeSelected);
    setSelectedLang(languageToBeSelected);
  };

  const countryFieldLabel = (country) => {
    const { iso, name, showLabel, label } = country;
    return (
      <div className="btn-flex d-flex align-items-center px-4">
        {!hideCountryFlag && (
          <CountryFlag
            key={iso}
            country={country}
            type={type}
            className="img-flag h-100"
            flagWidth={flagWidth}
            flagHeight={flagHeight}
          />
        )}
        <div
          className={`text-truncate fs-10 lh-1-2 ls-1 country-label ${countryDD.countryLabel} ${
            hideCountryFlag ? "country-label-without-flag" : "country-label-with-flag"
          }`}
        >
          {showLabel ? label : name}
        </div>
      </div>
    );
  };

  const countryOptions = countries.map((country) => ({
    key: country.iso,
    label: countryFieldLabel(country),
    name: country.name,
  }));
  const currencyOptions = currencies?.map((currency) => ({
    ...currency,
    key: currency.code,
    label: currency.code,
    name: currency.code,
    fullName: currency.name,
  }));
  const forceTranslations = isForceTranslationsEnabled(router.query);

  useEffect(() => {
    // ToDo: This useEffect logic should be conditional in order to not trigger infinite redirections
    if (availableLanguages.length) {
      const languageToBeSelected = getSelectedLanguage({
        weglotLanguages: availableLanguages,
        langInUrl,
        weglotInstance,
        forceTranslations,
      });
      handleLanguageSelection(languageToBeSelected);
    }
  }, [availableLanguages, weglotInstance, forceTranslations]);

  useEffect(() => {
    setCountriesToBeDisplayed(countryOptions);
  }, [countries]);

  useEffect(() => {
    setCurrenciesToBeDisplayed(currencyOptions);
  }, [currencies]);

  useEffect(() => {
    const isClickedOutside = ({ target }) => {
      if (!target.closest(`#${DROPDOWN_ID}`)) {
        setIsDropDownOpen(false);
      }
    };
    window?.addEventListener("click", isClickedOutside);
    return () => window?.removeEventListener("click", isClickedOutside);
  }, []);

  const data = [
    ...(showLanguages
      ? [
          {
            label: "Translate Page To",
            items: availableLanguages,
            key: "languageTranslator",
            onChange: ({ id: value }) => {
              if (selectedLang !== value) {
                handleLanguageSelection(value);
                setIsDropDownOpen(false);
              }
            },
            selectedIds: [selectedLang],
            type: "radioButton",
            isRadioContent: false,
            loader: {
              text: availableLanguages?.length ? "" : "Loading Languages",
              display: !availableLanguages?.length,
            },
            wrapperClass: weglotNoTranslationClassName,
          },
        ]
      : []),
    {
      label: "Delivering To",
      items: countriesToBeDisplayed || countryOptions,
      key: "countryList",
      onChange: ({ id: iso }) => {
        const countrySelected = countries.filter((country) => country.iso === iso);
        setSelectedCountry(countrySelected[0]);
        !showCountryChangeModal && onSelectCountry(countrySelected[0]);
        setIsOpen(showCountryChangeModal);
        setIsDropDownOpen(false);
      },
      selectedIds: [confirmedCountry?.iso],
      type: "radioButton",
      isRadioContent: false,
      onFilter,
      showSearchFilter,
      attributesForSearch: ["name"],
      searchData: countryOptions,
      searchfilterOptions: { attributes: ["name"], placeholder: "Search Location" },
      includeNoResultForSearch: true,
    },
    currencies?.length > 0 && {
      label: "Display prices in",
      items: currenciesToBeDisplayed || currencyOptions,
      key: "currencyList",
      onChange: ({ id }) => {
        const currencySelected = currencies.find((currency) => currency.code === id);
        currencySelectorProps.setCurrency(currencySelected);
        setIsDropDownOpen(false);
      },
      selectedIds: [currencySelectorProps.currency?.code],
      type: "radioButton",
      isRadioContent: false,
      onFilter: onCurrencyFilter,
      showSearchFilter,
      attributesForSearch: ["name", "fullName"],
      searchData: currencyOptions,
      searchfilterOptions: { attributes: ["name", "fullName"], placeholder: "Search Currency" },
      includeNoResultForSearch: true,
    },
  ].filter(Boolean);

  return (
    <>
      <BsDropdownMultiSelect
        id={DROPDOWN_ID}
        className={styles.countryDropDownNew}
        label={
          dropdownLabel ?? (
            <>
              {labelText}
              {hideCountryFlag ? (
                <Pill color="ice" className={styles.isoPill}>
                  {defaultCountry?.iso}
                </Pill>
              ) : (
                <CountryFlag
                  country={defaultCountry}
                  type={type}
                  flagWidth={flagWidth}
                  flagHeight={flagHeight}
                />
              )}
              <i
                className={`icon-chevron-down ml-2 icon-chevron-down-new-postion ${arrowCustomClass}`}
              />
            </>
          )
        }
        isParentLabelRequired={!isDesktopDevice}
        data={data}
        isDropDownOpen={isDropDownOpen}
        parentLabel={parentLabel || "Select Location and Language"}
        onToggle={handleToggle}
        onClick={handleClick}
        itemsListBlock={styles.countryDropDownList}
      >
        {dropdownChildren}
      </BsDropdownMultiSelect>
      <NewCountrySelectorModal
        open={isOpen}
        closeModal={() => setIsOpen(false)}
        onClickSubmit={submitModal}
        title={title}
      />
    </>
  );
};

export default CountrySelector;
